// Breathing room kept between the tooltip and the edge of a clipping ancestor.
const CLIP_MARGIN = 8;

// How far a touch has to travel before it counts as a scroll rather than a tap.
const SCROLL_THRESHOLD = 10;

class InfoTooltip extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (e) => {
      const summary = e.target.closest("summary");
      if (summary !== this.summary) return;

      e.preventDefault();
      this.details.open ? this.close() : this.open();
    });

    this.addEventListener("mouseenter", (e) => {
      this.open();
    });

    this.addEventListener("mouseleave", (e) => {
      clearTimeout(this.closeTimeout);

      this.closeTimeout = window.setTimeout(() => this.close(), 1000);
    });

    this.addEventListener("focusout", (e) => {
      if (this.contains(e.relatedTarget)) return;
      this.close();
    });

    this.handleResize = () => {
      if (this.details?.open) this.align();
    };

    this.handleTouchStart = (e) => {
      const touch = e.touches[0];

      this.touchOrigin =
        touch && !this.contains(e.target)
          ? { x: touch.clientX, y: touch.clientY }
          : null;
    };

    // Touch devices get no mouseleave, so a scroll would otherwise leave the
    // tooltip open until the next tap.
    this.handleTouchMove = (e) => {
      if (!this.touchOrigin) return;

      const touch = e.touches[0];
      if (!touch) return;

      const distance = Math.hypot(
        touch.clientX - this.touchOrigin.x,
        touch.clientY - this.touchOrigin.y,
      );

      if (distance < SCROLL_THRESHOLD) return;

      this.close();
    };
  }

  connectedCallback() {
    // Capture the authored alignment once. align() writes to data-alignment, so
    // re-reading the attribute later would feed our own computed value back in
    // as the preference. Runs on connect rather than in the constructor because
    // preact sets attributes on the element before inserting it.
    if (!this.preferredAlignment) {
      this.preferredAlignment =
        this.dataset.alignment === "right" ? "right" : "left";
    }

    window.addEventListener("resize", this.handleResize);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.handleResize);
    this.unbindTouchScroll();
  }

  bindTouchScroll() {
    this.touchOrigin = null;

    document.addEventListener("touchstart", this.handleTouchStart, {
      capture: true,
      passive: true,
    });
    document.addEventListener("touchmove", this.handleTouchMove, {
      capture: true,
      passive: true,
    });
  }

  unbindTouchScroll() {
    this.touchOrigin = null;

    document.removeEventListener("touchstart", this.handleTouchStart, true);
    document.removeEventListener("touchmove", this.handleTouchMove, true);
  }

  // Pick the side that keeps the content on screen, then nudge it back into
  // view if neither side fits. Content lives inside a closed <details> until
  // open, so it has no box to measure — this has to run on each open, not once
  // up front.
  align() {
    const { content } = this;
    if (!content) return;

    const { bounds } = this;

    this.dataset.alignment = this.preferredAlignment;
    content.style.setProperty("--shift", "0px");

    let overflow = this.overflowOf(content, bounds);

    if (overflow !== 0) {
      this.dataset.alignment =
        this.preferredAlignment === "left" ? "right" : "left";

      const flippedOverflow = this.overflowOf(content, bounds);

      // Keep the flip only if it actually helps — a trigger near the middle of
      // a narrow viewport can overflow either way.
      if (Math.abs(flippedOverflow) < Math.abs(overflow)) {
        overflow = flippedOverflow;
      } else {
        this.dataset.alignment = this.preferredAlignment;
      }
    }

    content.style.setProperty("--shift", `${-overflow}px`);
  }

  // Positive = hangs off the right edge, negative = off the left, 0 = fits.
  overflowOf(element, bounds) {
    const { left, right } = element.getBoundingClientRect();

    if (right > bounds.right) return right - bounds.right;
    if (left < bounds.left) return left - bounds.left;

    return 0;
  }

  // The band the tooltip has to stay inside: the viewport, tightened by every
  // clipping ancestor. The viewport alone isn't enough — the quickview drawer
  // is 375px wide, so a tooltip can sit well inside the screen and still be cut
  // off by the panel scrolling it.
  get bounds() {
    const margin = this.viewportMargin;
    let left = margin;
    let right = window.innerWidth - margin;

    for (let el = this.parentElement; el; el = el.parentElement) {
      if (getComputedStyle(el).overflowX === "visible") continue;

      const rect = el.getBoundingClientRect();

      left = Math.max(left, rect.left + CLIP_MARGIN);
      right = Math.min(right, rect.right - CLIP_MARGIN);
    }

    return { left, right };
  }

  get viewportMargin() {
    const gutter = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue("--gutter"),
    );

    return isFinite(gutter) ? gutter : 16;
  }

  open() {
    clearTimeout(this.closeTimeout);

    if (this.details.open || this.transitioning) return;
    this.transitioning = true;

    this.removeEventListener("transitionend", this.handleTransitionEnd);

    this.handleTransitionEnd = () => {
      this.removeEventListener("transitionend", this.handleTransitionEnd);
      this.transitioning = false;
    };

    this.addEventListener("transitionend", this.handleTransitionEnd);

    this.details.open = true;
    this.bindTouchScroll();

    // Safe to measure/reposition here: the content is laid out but still at
    // opacity 0, so the correction lands before the fade-in starts.
    this.align();

    window.requestAnimationFrame(() => {
      this.details.classList.add("open");
    });
  }

  close() {
    if (!this.details.open || this.transitioning) return;
    this.transitioning = true;
    this.unbindTouchScroll();

    if (this.content) {
      this.removeEventListener("transitionend", this.handleTransitionEnd);

      this.handleTransitionEnd = () => {
        this.removeEventListener("transitionend", this.handleTransitionEnd);
        this.details.open = false;
        this.transitioning = false;
      };

      this.addEventListener("transitionend", this.handleTransitionEnd);

      this.details.classList.remove("open");
    } else {
      this.details.classList.remove("open");
      this.details.open = false;
      this.transitioning = false;
    }
  }

  get summary() {
    return this.querySelector("summary");
  }

  get details() {
    return this.querySelector("details");
  }

  get content() {
    return this.querySelector("[data-content]");
  }
}

if (!customElements.get("info-tooltip")) {
  customElements.define("info-tooltip", InfoTooltip);
}
